/**
 * DigestProfilesWorkspacePage — Manage digest profiles with multi-digest support
 * 
 * Features:
 * - Shows digest count (X/Y) based on user tier (Free: 1, Verified: 3, Pro: 5)
 * - Each digest labeled by specialty/subspecialty
 * - Filter digests by date or specialty
 * - Community room access based on digest specialties
 * - Create/edit/delete digest profiles
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { useAuth } from '../../contexts/AuthContext';
import { preferencesAPI, configAPI, digestsAPI } from '../../api';
import { toast } from 'sonner';
import {
  Plus,
  Settings,
  Trash2,
  Play,
  Calendar,
  Clock,
  Users,
  Stethoscope,
  ChevronRight,
  Loader2,
  Crown,
  CheckCircle2,
  AlertCircle,
  Filter,
  BookOpen,
} from 'lucide-react';

interface DigestProfile {
  profile_id: string;
  name: string;
  specialty_id: string;
  subspecialty_id?: string;
  subspecialties?: string[];
  topics_selected?: string[];
  digest_frequency: string;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  community_subspecialty_ids?: string[];
}

interface Digest {
  digest_id: string;
  profile_id?: string;
  created_at: string;
  article_count: number;
  status: string;
}

interface SpecialtyConfig {
  [key: string]: {
    label: string;
    subspecialties?: { [key: string]: { label: string } };
  };
}

const DigestProfilesWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<DigestProfile[]>([]);
  const [digests, setDigests] = useState<Digest[]>([]);
  const [specialtyConfig, setSpecialtyConfig] = useState<SpecialtyConfig>({});
  const [maxProfiles, setMaxProfiles] = useState(1);
  const [userTier, setUserTier] = useState<'free' | 'verified' | 'premium'>('free');
  
  // Filters
  const [filterSpecialty, setFilterSpecialty] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');
  
  // Actions
  const [runningProfileId, setRunningProfileId] = useState<string | null>(null);
  const [deletingProfileId, setDeletingProfileId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load specialty config
      const config = await configAPI.getSpecialties();
      setSpecialtyConfig(config as any);
      
      // Load profiles
      const profilesData = await preferencesAPI.listProfiles();
      setProfiles(profilesData?.profiles || []);
      setMaxProfiles(profilesData?.max_profiles || 1);
      
      // Determine user tier from max profiles
      if (profilesData?.max_profiles >= 5) {
        setUserTier('premium');
      } else if (profilesData?.max_profiles >= 3) {
        setUserTier('verified');
      } else {
        setUserTier('free');
      }
      
      // Load recent digests
      const digestsData = await digestsAPI.getMyDigests(20);
      // Map to local Digest type
      const mappedDigests: Digest[] = (digestsData?.digests || []).map((d: any) => ({
        digest_id: d.digest_id,
        profile_id: d.profile_id,
        created_at: d.generated_at || d.created_at || '',
        article_count: d.article_count || 0,
        status: d.status || 'completed',
      }));
      setDigests(mappedDigests);
      
    } catch (error: any) {
      console.error('Failed to load data:', error);
      // If profiles feature is disabled, fall back gracefully
      if (error.response?.data?.detail?.error_code === 'feature_disabled') {
        toast.info('Multi-digest profiles not available on your plan');
      } else {
        toast.error('Failed to load digest profiles');
      }
    } finally {
      setLoading(false);
    }
  };

  const getSpecialtyLabel = (specialtyId: string) => {
    return specialtyConfig[specialtyId]?.label || specialtyId;
  };

  const getSubspecialtyLabel = (specialtyId: string, subspecialtyId: string) => {
    const specialty = specialtyConfig[specialtyId];
    if (!specialty?.subspecialties) return subspecialtyId;
    return specialty.subspecialties[subspecialtyId]?.label || subspecialtyId;
  };

  const handleRunDigest = async (profileId: string) => {
    try {
      setRunningProfileId(profileId);
      const result = await digestsAPI.runDigestNow(false, profileId);
      if (result.article_count && result.article_count > 0) {
        toast.success(`Generated ${result.article_count} articles!`);
        await loadData();
      } else {
        toast.info('No new articles since your last digest.');
      }
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (detail?.error_code === 'run_now_quota_exceeded') {
        toast.error('Daily digest limit reached. Try again tomorrow.');
      } else {
        toast.error('Failed to run digest');
      }
    } finally {
      setRunningProfileId(null);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!window.confirm('Are you sure you want to delete this digest profile?')) return;
    
    try {
      setDeletingProfileId(profileId);
      await preferencesAPI.deleteProfile(profileId);
      toast.success('Digest profile deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete profile');
    } finally {
      setDeletingProfileId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatFrequency = (freq: string) => {
    const labels: Record<string, string> = {
      daily: 'Daily',
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
    };
    return labels[freq] || freq;
  };

  // Get unique specialties for filter
  const uniqueSpecialties = Array.from(new Set(profiles.map(p => p.specialty_id)));

  // Filter digests
  const filteredDigests = digests.filter(d => {
    if (filterSpecialty !== 'all') {
      const profile = profiles.find(p => p.profile_id === d.profile_id);
      if (!profile || profile.specialty_id !== filterSpecialty) return false;
    }
    if (filterDate !== 'all') {
      const digestDate = new Date(d.created_at);
      const now = new Date();
      const daysAgo = Math.floor((now.getTime() - digestDate.getTime()) / (1000 * 60 * 60 * 24));
      if (filterDate === '7' && daysAgo > 7) return false;
      if (filterDate === '30' && daysAgo > 30) return false;
      if (filterDate === '90' && daysAgo > 90) return false;
    }
    return true;
  });

  // Calculate community access count
  const communityAccessCount = profiles.reduce((count, p) => {
    // Count unique subspecialties across all profiles
    const subspecialties = p.community_subspecialty_ids || p.subspecialties || [];
    return count + subspecialties.length;
  }, 0);

  const getTierBadge = () => {
    if (userTier === 'premium') {
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          <Crown className="w-3 h-3 mr-1" />
          Pro
        </Badge>
      );
    }
    if (userTier === 'verified') {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Verified
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-100 text-gray-600 border-gray-200">
        Free
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto" data-testid="digest-profiles-page">
      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">
                Digest Profiles
              </h1>
              {getTierBadge()}
            </div>
            <p className="text-[hsl(var(--muted-foreground))]">
              Manage your personalized literature digests
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Profile Count Badge */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--muted))]">
              <BookOpen className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="font-semibold text-[hsl(var(--foreground))]">
                {profiles.length} / {maxProfiles}
              </span>
              <span className="text-sm text-[hsl(var(--muted-foreground))]">
                Digests
              </span>
            </div>
            
            {profiles.length < maxProfiles && (
              <Button 
                onClick={() => navigate('/preferences?mode=create')}
                data-testid="create-digest-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Digest
              </Button>
            )}
          </div>
        </div>

        {/* Community Access Card */}
        <Card className="bg-[hsl(var(--primary))]/5 border-[hsl(var(--primary))]/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary))]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <p className="font-medium text-[hsl(var(--foreground))]">
                    Community Access
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Access to {communityAccessCount} specialty {communityAccessCount === 1 ? 'room' : 'rooms'} based on your digest selections
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/community')}
                data-testid="browse-communities-btn"
              >
                Browse LitClub
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Digest Profiles Grid */}
        <div>
          <h2 className="text-lg font-semibold text-[hsl(var(--foreground))] mb-4">
            Your Digests
          </h2>
          
          {profiles.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--muted))] mx-auto mb-4 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />
              </div>
              <h3 className="font-semibold text-[hsl(var(--foreground))] mb-2">
                No Digest Profiles Yet
              </h3>
              <p className="text-[hsl(var(--muted-foreground))] mb-4">
                Create your first digest to start receiving personalized literature updates.
              </p>
              <Button onClick={() => navigate('/onboarding/preferences')}>
                <Plus className="w-4 h-4 mr-2" />
                Set Up Your First Digest
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map((profile) => (
                <Card 
                  key={profile.profile_id}
                  className={`relative overflow-hidden transition-all hover:shadow-md ${
                    profile.is_primary ? 'ring-2 ring-[hsl(var(--primary))]' : ''
                  }`}
                  data-testid={`digest-profile-${profile.profile_id}`}
                >
                  {profile.is_primary && (
                    <div className="absolute top-0 right-0 bg-[hsl(var(--primary))] text-white text-xs px-2 py-0.5 rounded-bl">
                      Primary
                    </div>
                  )}
                  
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Stethoscope className="w-4 h-4 text-[hsl(var(--primary))]" />
                      {getSpecialtyLabel(profile.specialty_id)}
                    </CardTitle>
                    <CardDescription>
                      {profile.subspecialties && profile.subspecialties.length > 0 ? (
                        <span className="line-clamp-1">
                          {profile.subspecialties.map(s => 
                            getSubspecialtyLabel(profile.specialty_id, s)
                          ).join(', ')}
                        </span>
                      ) : profile.subspecialty_id ? (
                        getSubspecialtyLabel(profile.specialty_id, profile.subspecialty_id)
                      ) : (
                        'All subspecialties'
                      )}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-0 space-y-3">
                    {/* Topics */}
                    {profile.topics_selected && profile.topics_selected.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {profile.topics_selected.slice(0, 3).map((topic, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                        {profile.topics_selected.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{profile.topics_selected.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Schedule */}
                    <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                      <Clock className="w-3.5 h-3.5" />
                      {formatFrequency(profile.digest_frequency)}
                    </div>
                    
                    {/* Community Access */}
                    {(profile.community_subspecialty_ids?.length || 0) > 0 && (
                      <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                        <Users className="w-3.5 h-3.5" />
                        {profile.community_subspecialty_ids?.length} community {profile.community_subspecialty_ids?.length === 1 ? 'room' : 'rooms'}
                      </div>
                    )}
                    
                    <Separator />
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleRunDigest(profile.profile_id)}
                        disabled={runningProfileId === profile.profile_id}
                        data-testid={`run-digest-${profile.profile_id}`}
                      >
                        {runningProfileId === profile.profile_id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Play className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Run Now
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/preferences?profile=${profile.profile_id}`)}
                        data-testid={`edit-digest-${profile.profile_id}`}
                      >
                        <Settings className="w-3.5 h-3.5" />
                      </Button>
                      {!profile.is_primary && profiles.length > 1 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteProfile(profile.profile_id)}
                          disabled={deletingProfileId === profile.profile_id}
                          data-testid={`delete-digest-${profile.profile_id}`}
                        >
                          {deletingProfileId === profile.profile_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Add New Profile Card */}
              {profiles.length < maxProfiles && (
                <Card 
                  className="border-dashed border-2 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 transition-colors cursor-pointer"
                  onClick={() => navigate('/preferences?mode=create')}
                  data-testid="add-digest-card"
                >
                  <CardContent className="h-full flex flex-col items-center justify-center py-8">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center mb-3">
                      <Plus className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
                    </div>
                    <p className="font-medium text-[hsl(var(--foreground))]">
                      Add Another Digest
                    </p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      {maxProfiles - profiles.length} slot{maxProfiles - profiles.length !== 1 ? 's' : ''} remaining
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Recent Digest History */}
        {digests.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[hsl(var(--foreground))]">
                Digest History
              </h2>
              
              {/* Filters */}
              <div className="flex items-center gap-2">
                <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
                  <SelectTrigger className="w-[160px]">
                    <Filter className="w-3.5 h-3.5 mr-2" />
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    {uniqueSpecialties.map(s => (
                      <SelectItem key={s} value={s}>
                        {getSpecialtyLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterDate} onValueChange={setFilterDate}>
                  <SelectTrigger className="w-[140px]">
                    <Calendar className="w-3.5 h-3.5 mr-2" />
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Card>
              <ScrollArea className="h-[300px]">
                <div className="divide-y divide-[hsl(var(--border))]">
                  {filteredDigests.map((digest) => {
                    const profile = profiles.find(p => p.profile_id === digest.profile_id);
                    return (
                      <div 
                        key={digest.digest_id}
                        className="p-4 hover:bg-[hsl(var(--muted))]/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/screen?digest=${digest.digest_id}`)}
                        data-testid={`digest-${digest.digest_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                              <BookOpen className="w-5 h-5 text-[hsl(var(--primary))]" />
                            </div>
                            <div>
                              <p className="font-medium text-[hsl(var(--foreground))]">
                                {profile ? getSpecialtyLabel(profile.specialty_id) : 'Digest'}
                              </p>
                              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                                {formatDate(digest.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant={digest.status === 'completed' ? 'default' : 'secondary'}>
                              {digest.article_count} articles
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredDigests.length === 0 && (
                    <div className="p-8 text-center text-[hsl(var(--muted-foreground))]">
                      No digests match your filters
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </div>
        )}

        {/* Upgrade Prompt for Free Users */}
        {userTier === 'free' && profiles.length >= maxProfiles && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900">
                    Want more digests?
                  </h3>
                  <p className="text-sm text-amber-700">
                    Upgrade to Pro for up to 5 digests and access to 15 community rooms.
                  </p>
                </div>
                <Button 
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DigestProfilesWorkspacePage;
